import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  TextField,
  Button,
} from '@material-ui/core';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Timeline as TimelineIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  DragIndicator as DragIcon,
  ChatBubbleOutline as GreetingIcon,
} from '@material-ui/icons';
import { ChromePicker } from 'react-color';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory, useParams } from 'react-router-dom';
import usePipelines from '../../hooks/usePipelines';
import Title from '../../components/Title';
import ColorPicker from '../../components/ColorPicker';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    fontWeight: 'bold',
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
  },
  tagChip: {
    margin: theme.spacing(0.5),
    cursor: 'pointer',
  },
  addTagButton: {
    margin: theme.spacing(0.5),
    borderStyle: 'dashed',
  },
  colorPreview: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    border: '2px solid #ddd',
    cursor: 'pointer',
    display: 'inline-block',
    marginRight: theme.spacing(1),
  },
  userAvatar: {
    width: 32,
    height: 32,
    fontSize: '0.8rem',
  },
  permissionCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  dangerZone: {
    border: '2px solid #f44336',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: '#ffebee',
  },
  deleteButton: {
    backgroundColor: '#f44336',
    color: 'white',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  },
  stageItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5),
    margin: theme.spacing(0.5, 0),
    border: '1px solid #e0e0e0',
    borderRadius: theme.spacing(1),
    '&:hover': {
      opacity: 0.7,
    },
    // Estilos para drag and drop
    '&[data-rbd-draggable-context-id]': {
      cursor: 'grab',
    },
    '&[data-rbd-draggable-context-id]:active': {
      cursor: 'grabbing',
    },
  },
  stageOrder: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2),
    color: '#666',
    fontWeight: 'bold',
    minWidth: 30,
  },
  stageContent: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
  },
  stageColor: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    marginRight: theme.spacing(1),
    border: '1px solid #ddd',
  },
  stageName: {
    fontWeight: '500',
  },
  stageActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  reorderButton: {
    padding: theme.spacing(0.5),
    minWidth: 'auto',
    '&:disabled': {
      opacity: 0.3,
    },
  },
  dragHandle: {
    cursor: 'grab',
    color: '#999',
    marginRight: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 24,
    height: 24,
    '&:active': {
      cursor: 'grabbing',
    },
  },
  dragHandleDisabled: {
    cursor: 'not-allowed',
    color: '#ccc',
    marginRight: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 24,
    height: 24,
    opacity: 0.5,
  },
  droppableArea: {
    minHeight: 50,
  },
}));

const CrmConfig = () => {
  const classes = useStyles();
  const history = useHistory();
  const params = useParams();
  const { user } = useContext(AuthContext);
  const { profile } = user;

  const { id: pipelineId } = params; // Extrair 'id' da rota e renomear para pipelineId
  const { update: updatePipeline } = usePipelines();

  // Estados do pipeline (greetingMessage: mensagem de saudação ao transferir lead para fila desta pipeline)
  const [pipeline, setPipeline] = useState({
    id: parseInt(pipelineId) || null,
    name: 'Carregando...',
    description: '',
    companyId: null,
    queueId: null,
    isActive: true,
    createdAt: null,
    updatedAt: null,
    users: [],
    stages: [],
    greetingMessage: '',
  });

  // Estados para modais
  const [tagDialog, setTagDialog] = useState(false);
  // const [userDialog, setUserDialog] = useState(false); // Removido - não utilizado
  const [colorPicker, setColorPicker] = useState(false);
  const [newTag, setNewTag] = useState({
    name: '',
    color: '#2196f3',
    description: '',
  });
  // const [selectedUsers, setSelectedUsers] = useState([]); // Removido - não utilizado
  const [loading, setLoading] = useState(true);
  const [creatingStage, setCreatingStage] = useState(false);


  // Estado para forçar re-renderização do componente quando a reordenação falhar
  const [componentKey, setComponentKey] = useState(0);

  // Estado para controlar atualizações dos stages
  const [stagesUpdated, setStagesUpdated] = useState(0);
  const [savingGreeting, setSavingGreeting] = useState(false);


  // Carregar dados da pipeline
  useEffect(() => {
    const loadPipeline = async () => {
      setLoading(true);

      // Timeout para evitar carregamento infinito
      const timeoutId = setTimeout(() => {
        const fallbackPipeline = {
          id: parseInt(pipelineId),
          name: 'Pipeline de Teste (Timeout)',
          description: 'Pipeline para testes - timeout na API',
          companyId: 1,
          queueId: 1,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          users: [],
          stages: [
            {
              id: 1,
              name: 'Prospecção',
              description: 'Primeira etapa',
              color: '#2196f3',
              order: 1,
            },
            {
              id: 2,
              name: 'Negociação',
              description: 'Segunda etapa',
              color: '#ff9800',
              order: 2,
            },
            {
              id: 3,
              name: 'Fechamento',
              description: 'Terceira etapa',
              color: '#4caf50',
              order: 3,
            },
          ],
          greetingMessage: '',
        };
        setPipeline(fallbackPipeline);
        setLoading(false);
      }, 5000); // 5 segundos de timeout

      try {
        const { data } = await api.get(`/pipelines/${pipelineId}`);
        clearTimeout(timeoutId); // Limpar timeout se API responder

        // Verificar se stages existe e é um array
        if (!data.stages || !Array.isArray(data.stages)) {
          data.stages = [];
        }

        // Transformar stages em tags para manter compatibilidade
        const stagesWithOrder = data.stages
          .sort((a, b) => a.order - b.order)
          .map(stage => ({
            ...stage,
          }));

        setPipeline({
          ...data,
          stages: stagesWithOrder,
        });
      } catch (error) {
        clearTimeout(timeoutId); // Limpar timeout em caso de erro
        toast.error('Erro ao carregar dados da pipeline');

        // Em caso de erro, usar dados mockados como fallback
        const fallbackPipeline = {
          id: parseInt(pipelineId),
          name: 'Pipeline de Teste (Erro)',
          description: 'Pipeline para testes - erro na API',
          companyId: 1,
          queueId: 1,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          users: [],
          stages: [
            {
              id: 1,
              name: 'Prospecção',
              description: 'Primeira etapa',
              color: '#2196f3',
              order: 1,
            },
            {
              id: 2,
              name: 'Negociação',
              description: 'Segunda etapa',
              color: '#ff9800',
              order: 2,
            },
            {
              id: 3,
              name: 'Fechamento',
              description: 'Terceira etapa',
              color: '#4caf50',
              order: 3,
            },
          ],
          greetingMessage: '',
        };

        setPipeline(fallbackPipeline);
      } finally {
        setLoading(false);
      }
    };

    if (pipelineId) {
      loadPipeline();
    }
  }, [pipelineId, componentKey]); // ✅ Adicionado componentKey como dependência

  // useEffect para monitorar mudanças nos stages e forçar re-renderização
  useEffect(() => {
    if (stagesUpdated > 0) {
      // Forçar uma pequena atualização para garantir que o React detecte as mudanças
      const timeoutId = setTimeout(() => {
        setComponentKey(prev => prev + 1);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [stagesUpdated]);

  const handleBack = () => {
    history.push('/crm');
  };

  const handleAddTag = async () => {
    if (newTag.name.trim()) {
      setCreatingStage(true);
      try {
        // 1. Definir a ordem antes de criar o estágio
        const order = pipeline.stages.length - 1;

        // 2. Criar o estágio via API
        const stageData = {
          name: newTag.name,
          description: newTag.description || newTag.name,
          color: newTag.color,
          pipelineId: pipelineId,
          order, // <<< ordem definida na criação
        };

        const { data: newStage } = await api.post('/stages', stageData);

        // A API retorna apenas o ID diretamente, não um objeto
        const stageId = typeof newStage === 'object' ? newStage.id : newStage;

        // Verificar se o ID retornado é válido
        if (!stageId) {
          throw new Error('ID do estágio não foi retornado pela API');
        }

        // 3. Vincular o estágio à pipeline
        const currentStageIds = pipeline.stages
          .map(stage => stage.id)
          .filter(id => id !== null && id !== undefined);

        const updatedStageIds = [...currentStageIds, stageId];

        // Garantir que não há duplicados
        const uniqueStageIds = [...new Set(updatedStageIds)];

        await api.patch(`/pipelines/${pipeline.id}`, {
          stages: uniqueStageIds,
        });

        // 4. Atualizar o estado local
        const newStageWithOrder = {
          id: stageId,
          name: newTag.name,
          description: newTag.description || newTag.name,
          color: newTag.color,
          order, // <<< já vem pronto
        };

        // Forçar atualização do estado com uma nova referência
        setPipeline(prev => {
          const updatedStages = [...prev.stages, newStageWithOrder];
          return {
            ...prev,
            stages: updatedStages,
          };
        });

        // 5. Forçar re-renderização do componente para garantir que os cards sejam atualizados
        setComponentKey(prev => prev + 1);
        setStagesUpdated(prev => prev + 1);

        // 6. Limpar formulário e fechar modal
        setNewTag({ name: '', color: '#2196f3', description: '' });
        setTagDialog(false);

        toast.success('Estágio criado e vinculado com sucesso!');
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Erro ao criar estágio. Tente novamente.';
        toast.error(errorMessage);
      } finally {
        setCreatingStage(false);
      }
    }
  };

  const handleDeleteTag = async tagId => {
    try {
      // 1. Deletar o estágio no backend
      await api.delete(`/stages/${tagId}`);

      setPipeline(prev => {
        // 2. Remover do estado local
        const updatedStages = prev.stages.filter(stage => stage.id !== tagId);

        // 3. Reordenar os estágios localmente
        const reorderedStages = updatedStages.map((stage, index) => ({
          ...stage,
          order: index + 1,
        }));

        // 4. Sincronizar ordem no backend
        syncStagesOrder(prev.id, reorderedStages);

        return {
          ...prev,
          stages: reorderedStages,
        };
      });

      // Forçar re-renderização do componente para garantir que os cards sejam atualizados
      setComponentKey(prev => prev + 1);
      setStagesUpdated(prev => prev + 1);

      toast.success(i18n.t('pipelines.config.tagDeleted'));
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Erro ao remover estágio.';
      toast.error(errorMessage);
      console.error(err);
    }
  };

  const handleEditStage = stageId => {
    // Navegar para a página de edição do estágio passando o queueId
    const queueId = pipeline.queueId;
    if (queueId) {
      history.push(`/stage/${stageId}/config`);
    } else {
      history.push(`/stage/${stageId}/config`);
    }
  };

  const handleSaveGreetingMessage = async () => {
    if (!pipeline.id) return;
    setSavingGreeting(true);
    try {
      const value = (pipeline.greetingMessage ?? '').trim();
      // Enviar apenas greetingMessage no PATCH. Não enviar description: a descrição da pipeline
      // é outro campo; a saudação deve ser persistida somente em greetingMessage.
      await updatePipeline(pipeline.id, {
        greetingMessage: value || null,
      });
      setPipeline(prev => ({ ...prev, greetingMessage: value || '' }));
      toast.success(i18n.t('pipelines.config.saved'));
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Erro ao salvar mensagem de saudação.';
      toast.error(errorMessage);
    } finally {
      setSavingGreeting(false);
    }
  };

  // Função para verificar se um estágio pode ser reordenado
  const canReorderStage = (stageIndex, totalStages) => {
    // Não pode reordenar o primeiro, último e penúltimo
    return stageIndex > 0 && stageIndex < totalStages - 2;
  };

  // Função para verificar se um estágio pode ser removido
  const canDeleteStage = (stageIndex, totalStages) => {
    // Não pode remover o primeiro, último e penúltimo
    return stageIndex > 0 && stageIndex < totalStages - 2;
  };

  const syncStagesOrder = async (pipelineId, stages) => {
    try {
      // Verificar se há stages que não podem ser reordenados
      const hasDefaultStages = stages.some(
        stage => stage.isDefault || stage.default
      );

      if (hasDefaultStages) {
        toast.warning('Não é possível reordenar stages padrão do sistema.');
        return;
      }

      // Criar array com os IDs dos stages em ordem
      const stageIds = stages.map(stage => stage.id);

      // Enviar objeto com propriedade stageIds contendo o array
      await api.post(`/pipelines/${pipelineId}/stages/reorder`, { stageIds });

      // Toast de sucesso apenas quando a API for bem-sucedida
      toast.success(i18n.t('pipelines.config.stageReordered'));
    } catch (err) {
      // Forçar re-renderização do componente para restaurar a ordem original
      setComponentKey(prev => prev + 1);

      if (err.response?.data?.error === 'ERR_CANNOT_REORDER_DEFAULT_STAGES') {
        toast.warning('Não é possível reordenar stages padrão do sistema.');
      } else {
        const errorMessage =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Erro ao atualizar a ordem dos estágios. Componente será recarregado.';
        toast.error(errorMessage);
        console.error('Erro ao reordenar stages:', err);
      }
    }
  };

  // Função para drag and drop
  const handleDragEnd = async result => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    setPipeline(prev => {
      const stages = [...prev.stages];

      // Verificar se algum dos stages envolvidos é padrão
      const sourceStage = stages[sourceIndex];
      const destinationStage = stages[destinationIndex];

      if (
        sourceStage?.isDefault ||
        sourceStage?.default ||
        destinationStage?.isDefault ||
        destinationStage?.default
      ) {
        toast.warning('Não é possível reordenar stages padrão do sistema.');
        return prev;
      }

      const [removed] = stages.splice(sourceIndex, 1);
      stages.splice(destinationIndex, 0, removed);

      // Atualizar a ordem
      const reorderedStages = stages.map((stage, index) => ({
        ...stage,
        order: index + 1,
      }));

      // Atualiza no backend
      syncStagesOrder(prev.id, reorderedStages);

      return { ...prev, stages: reorderedStages };
    });
  };

  // Funções para reordenação dos estágios (mantidas para compatibilidade)
  const moveStageUp = tagId => {
    setPipeline(prev => {
      const stages = [...prev.stages];
      const currentIndex = stages.findIndex(stage => stage.id === tagId);
      const currentStage = stages[currentIndex];

      // Verificar se o stage é padrão
      if (currentStage?.isDefault || currentStage?.default) {
        toast.warning('Não é possível reordenar stages padrão do sistema.');
        return prev;
      }

      if (currentIndex > 0) {
        const targetStage = stages[currentIndex - 1];

        // Verificar se o stage de destino também é padrão
        if (targetStage?.isDefault || targetStage?.default) {
          toast.warning('Não é possível reordenar stages padrão do sistema.');
          return prev;
        }

        [stages[currentIndex - 1], stages[currentIndex]] = [
          stages[currentIndex],
          stages[currentIndex - 1],
        ];

        const reorderedStages = stages.map((stage, index) => ({
          ...stage,
          order: index + 1,
        }));

        syncStagesOrder(prev.id, reorderedStages);

        return { ...prev, stages: reorderedStages };
      }
      return prev;
    });
  };

  const moveStageDown = tagId => {
    setPipeline(prev => {
      const stages = [...prev.stages];
      const currentIndex = stages.findIndex(stage => stage.id === tagId);
      const currentStage = stages[currentIndex];

      // Verificar se o stage é padrão
      if (currentStage?.isDefault || currentStage?.default) {
        toast.warning('Não é possível reordenar stages padrão do sistema.');
        return prev;
      }

      if (currentIndex < stages.length - 1) {
        const targetStage = stages[currentIndex + 1];

        // Verificar se o stage de destino também é padrão
        if (targetStage?.isDefault || targetStage?.default) {
          toast.warning('Não é possível reordenar stages padrão do sistema.');
          return prev;
        }

        [stages[currentIndex], stages[currentIndex + 1]] = [
          stages[currentIndex + 1],
          stages[currentIndex],
        ];

        const reorderedStages = stages.map((stage, index) => ({
          ...stage,
          order: index + 1,
        }));

        syncStagesOrder(prev.id, reorderedStages);

        return { ...prev, stages: reorderedStages };
      }
      return prev;
    });
  };

  // const handleAddUsers = () => {
  //   // Por enquanto, vamos usar dados mockados para usuários
  //   // Em uma implementação real, isso viria da API
  //   const mockUsers = [
  //     { id: 4, name: 'Ana Costa', email: 'ana@empresa.com' },
  //     { id: 5, name: 'Carlos Lima', email: 'carlos@empresa.com' },
  //     { id: 6, name: 'Lucia Ferreira', email: 'lucia@empresa.com' },
  //   ];

  //   const usersToAdd = selectedUsers.map(userId => {
  //     const user = mockUsers.find(u => u.id === userId);
  //     return { ...user, role: 'user' };
  //   });

  //   setPipeline(prev => ({
  //     ...prev,
  //     users: [...prev.users, ...usersToAdd],
  //   }));
  //   setSelectedUsers([]);
  //   setUserDialog(false);
  //   toast.success(i18n.t('pipelines.config.usersAdded'));
  // }; // Removido - não utilizado

  // const handleRemoveUser = userId => {
  //   setPipeline(prev => ({
  //     ...prev,
  //     users: prev.users.filter(user => user.id !== userId),
  //   }));
  //   toast.success(i18n.t('pipelines.config.userRemoved'));
  // }; // Removido - não utilizado

  // const handleDeletePipeline = () => {
  //   const confirmDelete = window.confirm(
  //     i18n.t('pipelines.config.confirmDelete', { name: pipeline.name })
  //   );
  //   if (confirmDelete) {
  //     toast.success(i18n.t('pipelines.config.deleted'));
  //     history.push('/crm');
  //   }
  // }; // Removido - não utilizado

  // Ordenar stages por ordem com chave única para forçar re-renderização
  const sortedStages = React.useMemo(() => {
    return [...pipeline.stages].sort((a, b) => a.order - b.order);
  }, [pipeline.stages]);

  if (loading && !pipeline.name) {
    return (
      <Container className={classes.root} maxWidth='md'>
        <div className={classes.header}>
          <IconButton className={classes.backButton} onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Title>Carregando pipeline...</Title>
        </div>
      </Container>
    );
  }

  return (
    <Container key={componentKey} className={classes.root} maxWidth='md'>
      {/* Header */}
      <div className={classes.header}>
        <IconButton className={classes.backButton} onClick={handleBack}>
          <ArrowBackIcon />
        </IconButton>
        <Title>
          {i18n.t('pipelines.config.title')} - {pipeline.name}
        </Title>
      </div>
      <Grid container>
        {/* Mensagem de saudação */}
        <Grid item xs={12}>
          <Card className={classes.section}>
            <CardContent>
              <Typography variant='h6' className={classes.sectionTitle}>
                <GreetingIcon className={classes.sectionIcon} />
                {i18n.t('pipelines.config.greetingMessage')}
              </Typography>
              <Typography variant='body2' color='textSecondary' paragraph>
                {i18n.t('pipelines.config.greetingMessageHelp')}
              </Typography>
              {/* Campo exclusivo para greetingMessage. Não usar pipeline.description aqui. */}
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                label={i18n.t('pipelines.config.greetingMessage')}
                value={pipeline.greetingMessage ?? ''}
                onChange={e =>
                  setPipeline(prev => ({
                    ...prev,
                    greetingMessage: e.target.value,
                  }))
                }
                placeholder={i18n.t('pipelines.config.greetingMessagePlaceholder')}
                margin='normal'
                variant='outlined'
              />
              <Box mt={2}>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleSaveGreetingMessage}
                  disabled={savingGreeting}
                >
                  {savingGreeting
                    ? i18n.t('pipelines.config.saving')
                    : i18n.t('pipelines.config.saveGreeting')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        {/* Estágios */}
        <Grid item xs={12}>
          <Card
            className={`${classes.section} ${profile === 'admin' ? classes.adminOnly : ''}`}
          >
            <CardContent>
              <Typography variant='h6' className={classes.sectionTitle}>
                <TimelineIcon className={classes.sectionIcon} />
                {i18n.t('pipelines.config.stages')}
              </Typography>

              {profile === 'admin' ? (
                // Visualização para administradores com drag and drop
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId='stages'>
                    {(provided, snapshot) => (
                      <Box
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className={classes.droppableArea}
                      >
                        {sortedStages.map((tag, index) => (
                          <React.Fragment
                            key={`${tag.id}-${componentKey}-${stagesUpdated}`}
                          >
                            <Draggable
                              draggableId={tag.id.toString()}
                              index={index}
                              isDragDisabled={
                                !canReorderStage(index, sortedStages.length)
                              }
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...(canReorderStage(
                                    index,
                                    sortedStages.length
                                  )
                                    ? provided.dragHandleProps
                                    : {})}
                                  className={classes.stageItem}
                                  style={{
                                    ...provided.draggableProps.style,
                                    boxShadow: snapshot.isDragging
                                      ? '0 5px 10px rgba(0,0,0,0.15)'
                                      : 'none',
                                    cursor: !canReorderStage(
                                      index,
                                      sortedStages.length
                                    )
                                      ? 'not-allowed'
                                      : 'grab',
                                  }}
                                >
                                  <div
                                    className={
                                      canReorderStage(
                                        index,
                                        sortedStages.length
                                      )
                                        ? classes.dragHandle
                                        : classes.dragHandleDisabled
                                    }
                                  >
                                    <DragIcon />
                                  </div>
                                  <div className={classes.stageOrder}>
                                    {tag.order}
                                  </div>
                                  <div className={classes.stageContent}>
                                    <div
                                      className={classes.stageColor}
                                      style={{ backgroundColor: tag.color }}
                                    />
                                    <Typography className={classes.stageName}>
                                      {tag.name}
                                    </Typography>
                                  </div>
                                  <div className={classes.stageActions}>
                                    <Tooltip
                                      title={i18n.t('pipelines.config.moveUp')}
                                      placement='top'
                                    >
                                      <IconButton
                                        className={classes.reorderButton}
                                        onClick={() => moveStageUp(tag.id)}
                                        disabled={
                                          !canReorderStage(
                                            index,
                                            sortedStages.length
                                          )
                                        }
                                        size='small'
                                        aria-label={i18n.t(
                                          'pipelines.config.moveUpAria'
                                        )}
                                      >
                                        <ArrowUpIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip
                                      title={i18n.t(
                                        'pipelines.config.moveDown'
                                      )}
                                      placement='top'
                                    >
                                      <IconButton
                                        className={classes.reorderButton}
                                        onClick={() => moveStageDown(tag.id)}
                                        disabled={
                                          !canReorderStage(
                                            index,
                                            sortedStages.length
                                          )
                                        }
                                        size='small'
                                        aria-label={i18n.t(
                                          'pipelines.config.moveDownAria'
                                        )}
                                      >
                                        <ArrowDownIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip
                                      title={i18n.t(
                                        'pipelines.config.editStage'
                                      )}
                                      placement='top'
                                    >
                                      <IconButton
                                        onClick={() => handleEditStage(tag.id)}
                                        size='small'
                                        aria-label={i18n.t(
                                          'pipelines.config.editStageAria'
                                        )}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip
                                      title={i18n.t(
                                        'pipelines.config.deleteStage'
                                      )}
                                      placement='top'
                                    >
                                      <IconButton
                                        onClick={() => handleDeleteTag(tag.id)}
                                        disabled={
                                          !canDeleteStage(
                                            index,
                                            sortedStages.length
                                          )
                                        }
                                        size='small'
                                        color='secondary'
                                        aria-label={i18n.t(
                                          'pipelines.config.deleteStageAria'
                                        )}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </div>
                                </div>
                              )}
                            </Draggable>

                            {/* Botão de adicionar estágio antes do penúltimo item */}
                            {profile === 'admin' &&
                              index === sortedStages.length - 3 && (
                                <Box
                                  key={`add-before-penultimate`}
                                  display='flex'
                                  justifyContent='center'
                                  my={1}
                                >
                                  <Chip
                                    label='Adicionar Estágio'
                                    className={classes.addTagButton}
                                    variant='outlined'
                                    icon={<AddIcon />}
                                    onClick={() => setTagDialog(true)}
                                    style={{
                                      borderStyle: 'dashed',
                                      backgroundColor: 'transparent',
                                      borderColor: '#ccc',
                                      color: '#666',
                                    }}
                                  />
                                </Box>
                              )}
                          </React.Fragment>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                // Visualização para usuários comuns
                <Box>
                  {sortedStages.map(tag => (
                    <Chip
                      key={tag.id}
                      label={`${tag.order}. ${tag.name}`}
                      className={classes.tagChip}
                      style={{ backgroundColor: tag.color, color: 'white' }}
                    />
                  ))}
                  {profile === 'admin' && (
                    <Chip
                      label={i18n.t('pipelines.config.addStage')}
                      className={classes.addTagButton}
                      variant='outlined'
                      icon={<AddIcon />}
                      onClick={() => setTagDialog(true)}
                      aria-label={i18n.t('pipelines.config.addStageAria')}
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Usuários */}
        {/* <Grid item xs={12}>
          <Card className={classes.section}>
            <CardContent>
              <Typography variant='h6' className={classes.sectionTitle}>
                <PeopleIcon className={classes.sectionIcon} />
                {i18n.t('pipelines.config.users')}
                <Button
                  size='small'
                  startIcon={<AddIcon />}
                  onClick={() => setUserDialog(true)}
                  style={{ marginLeft: 'auto' }}
                  aria-label={i18n.t('pipelines.config.addUserAria')}
                >
                  {i18n.t('pipelines.config.addUser')}
                </Button>
              </Typography>

              <List>
                {pipeline.users.map(user => (
                  <ListItem key={user.id}>
                    <Avatar className={classes.userAvatar}>
                      {user.name.charAt(0)}
                    </Avatar>
                    <ListItemText
                      primary={user.name}
                      secondary={`${user.email} - ${user.role}`}
                      style={{ marginLeft: 16 }}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip
                        title={i18n.t('pipelines.config.removeUser')}
                        placement='top'
                      >
                        <IconButton
                          edge='end'
                          onClick={() => handleRemoveUser(user.id)}
                          aria-label={i18n.t('pipelines.config.removeUserAria')}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid> */}

        {/* Zona de Perigo */}
        {/* {profile === 'admin' && (
          <Button
            variant='contained'
            className={classes.deleteButton}
            onClick={handleDeletePipeline}
            startIcon={<DeleteIcon />}
            aria-label={i18n.t('pipelines.config.deletePipelineAria')}
          >
            {i18n.t('pipelines.config.deletePipeline')}
          </Button>
        )} */}
      </Grid>
      {/* Dialog para adicionar Tag */}
      <Dialog
        open={tagDialog}
        onClose={() => {
          setTagDialog(false);
          setNewTag({ name: '', color: '#2196f3', description: '' });
        }}
      >
        <DialogTitle>Criar Novo Estágio</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label='Nome do Estágio'
            value={newTag.name}
            onChange={e =>
              setNewTag(prev => ({ ...prev, name: e.target.value }))
            }
            margin='normal'
            required
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label='Descrição'
            value={newTag.description}
            onChange={e =>
              setNewTag(prev => ({ ...prev, description: e.target.value }))
            }
            margin='normal'
            placeholder='Descreva o propósito deste estágio...'
          />
          <Box display='flex' alignItems='center' mt={2}>
            <Typography variant='body2' style={{ marginRight: 16 }}>
              Cor:
            </Typography>
            <div
              className={classes.colorPreview}
              style={{ backgroundColor: newTag.color }}
              onClick={() => setColorPicker(!colorPicker)}
            />
            {colorPicker && (
              <ColorPicker
                currentColor={newTag.color}
                onChange={color => {
                  setNewTag(prev => ({ ...prev, color: color }));
                  setColorPicker(false);
                }}
                handleClose={() => setColorPicker(false)}
                open={colorPicker}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTagDialog(false);
              setNewTag({ name: '', color: '#2196f3', description: '' });
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleAddTag}
            color='primary'
            disabled={!newTag.name.trim() || creatingStage}
          >
            {creatingStage ? 'Criando...' : 'Criar Estágio'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para adicionar Usuários
      <Dialog open={userDialog} onClose={() => setUserDialog(false)}>
        <DialogTitle>{i18n.t('pipelines.config.addUser')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin='normal'>
            <InputLabel>{i18n.t('pipelines.config.selectUsers')}</InputLabel>
            <Select
              multiple
              value={selectedUsers}
              onChange={e => setSelectedUsers(e.target.value)}
              renderValue={selected =>
                selected.length + ' ' + i18n.t('pipelines.config.selected')
              }
              aria-label={i18n.t('pipelines.config.selectUsersAria')}
            >
              {[
                { id: 4, name: 'Ana Costa', email: 'ana@empresa.com' },
                { id: 5, name: 'Carlos Lima', email: 'carlos@empresa.com' },
                { id: 6, name: 'Lucia Ferreira', email: 'lucia@empresa.com' },
              ].map(user => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name} - {user.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUserDialog(false)}
            aria-label={i18n.t('common.cancelAria')}
          >
            {i18n.t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddUsers}
            color='primary'
            aria-label={i18n.t('common.addAria')}
          >
            {i18n.t('common.add')}
          </Button>
        </DialogActions>
      </Dialog> */}
    </Container>
  );
};

export default CrmConfig;
